import { type LocalizationBoundingBox, type LocalizationImageDimensions } from './localizationTypes';

const MIN_PROTECTED_REGION_SIZE_PX = 12;

export const boxesOverlap = (a: LocalizationBoundingBox, b: LocalizationBoundingBox): boolean =>
  a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y;

export const isPointInBoundingBox = (point: { x: number; y: number }, box: LocalizationBoundingBox): boolean =>
  point.x >= box.x && point.x <= box.x + box.width && point.y >= box.y && point.y <= box.y + box.height;

/** Store regions as 0–1 ratios so they stay aligned when OCR reports real image dimensions. */
export const normalizeBoundingBox = (
  box: LocalizationBoundingBox,
  imageDimensions: LocalizationImageDimensions,
): LocalizationBoundingBox => ({
  height: box.height / imageDimensions.height,
  width: box.width / imageDimensions.width,
  x: box.x / imageDimensions.width,
  y: box.y / imageDimensions.height,
});

export const denormalizeBoundingBox = (
  box: LocalizationBoundingBox,
  imageDimensions: LocalizationImageDimensions,
): LocalizationBoundingBox => ({
  height: box.height * imageDimensions.height,
  width: box.width * imageDimensions.width,
  x: box.x * imageDimensions.width,
  y: box.y * imageDimensions.height,
});

/** Map a screen point into image pixel space using the object-cover wrapper's on-screen rect. */
export const clientPointToImageCoordsFromDisplayRect = (
  clientX: number,
  clientY: number,
  displayRect: DOMRect,
  imageDimensions: LocalizationImageDimensions,
): { x: number; y: number } | null => {
  const withinX = clientX - displayRect.left;
  const withinY = clientY - displayRect.top;

  if (withinX < 0 || withinY < 0 || withinX > displayRect.width || withinY > displayRect.height) {
    return null;
  }

  return {
    x: (withinX / displayRect.width) * imageDimensions.width,
    y: (withinY / displayRect.height) * imageDimensions.height,
  };
};

export const buildBoundingBoxFromImagePoints = (
  start: { x: number; y: number },
  end: { x: number; y: number },
  imageDimensions: LocalizationImageDimensions,
): LocalizationBoundingBox | null => {
  const x = Math.max(0, Math.min(start.x, end.x));
  const y = Math.max(0, Math.min(start.y, end.y));
  const width = Math.min(Math.abs(end.x - start.x), imageDimensions.width - x);
  const height = Math.min(Math.abs(end.y - start.y), imageDimensions.height - y);

  if (width < MIN_PROTECTED_REGION_SIZE_PX || height < MIN_PROTECTED_REGION_SIZE_PX) {
    return null;
  }

  return { height, width, x, y };
};

const boxCenter = (box: LocalizationBoundingBox): { x: number; y: number } => ({
  x: box.x + box.width / 2,
  y: box.y + box.height / 2,
});

const overlapWidth = (a: LocalizationBoundingBox, b: LocalizationBoundingBox): number =>
  Math.max(0, Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x));

const overlapHeight = (a: LocalizationBoundingBox, b: LocalizationBoundingBox): number =>
  Math.max(0, Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y));

/** Text sits in horizontal bands, so vertical overlap is what says the drag is on this line at all: a full-width band clipping a neighbouring line's descenders would otherwise cover enough area to protect its words. */
const PROTECTED_VERTICAL_RATIO = 0.6;
/** Of the box's own width, since a drag that takes most of a word's height but little of its width was aimed at a neighbour. */
const PROTECTED_HORIZONTAL_RATIO = 0.4;

const verticalFraction = (box: LocalizationBoundingBox, regionBox: LocalizationBoundingBox): number =>
  box.height > 0 ? overlapHeight(box, regionBox) / box.height : 0;

const horizontalFraction = (box: LocalizationBoundingBox, regionBox: LocalizationBoundingBox): number =>
  box.width > 0 ? overlapWidth(box, regionBox) / box.width : 0;

/** The vertical gate also guards the centre test: without it, a band covering only the middle of a line's height would still contain every word's centre and protect the whole line. */
export const isWordProtectedByRegion = (word: LocalizationBoundingBox, regionBox: LocalizationBoundingBox): boolean =>
  verticalFraction(word, regionBox) >= PROTECTED_VERTICAL_RATIO &&
  (horizontalFraction(word, regionBox) >= PROTECTED_HORIZONTAL_RATIO ||
    isPointInBoundingBox(boxCenter(word), regionBox));

/** Omits the centre test: a line's midpoint is arbitrary, so testing it would let the same small drag protect a whole headline in one spot and nothing a few hundred pixels away. */
export const isLineProtectedByRegion = (line: LocalizationBoundingBox, regionBox: LocalizationBoundingBox): boolean =>
  verticalFraction(line, regionBox) >= PROTECTED_VERTICAL_RATIO &&
  horizontalFraction(line, regionBox) >= PROTECTED_HORIZONTAL_RATIO;
